import { computed, ref } from 'vue'
import moment from 'moment'

/**
 * Компонент (composable) для управления выбором даты.
 */
export function useDatePicker() {
  const dateInternal = ref(null)
  const dateTime = ref(null)
  const datePicker = ref(null)
  const timeSetFirstTime = ref(true)
  const initialDate = ref(null)
  const datePickerRef = ref(null)

  /**
   * Обработчик установки даты.
   * Проверяет, является ли значение валидной датой.
   * Если дата невалидна — можно либо прервать установку, либо подставить дату "по умолчанию".
   */
  const handleInternal = (value) => {
    // Если вообще никакого значения не пришло, просто выходим
    if (!value) {
      return
    }

    // Преобразуем к Date, если это не Date
    if (!(value instanceof Date)) {
      const parsedDate = new Date(value)

      // Проверяем, валидна ли дата
      if (isNaN(parsedDate.getTime())) {
        // Если дата невалидна, прерываем или устанавливаем свою логику
        // Например, можно просто выйти:
        return

        // Или подставить "дату по умолчанию" (текущий момент):
        // value = new Date()
      } else {
        value = parsedDate
      }
    }

    // Теперь value гарантированно объект Date
    if (timeSetFirstTime.value) {
      // При первом выставлении сбрасываем часы/минуты в 00:00
      initialDate.value = new Date(value.setHours(0, 0))
      datePicker.value = initialDate.value
      timeSetFirstTime.value = false
    } else {
      // Если уже выставляли время, просто сохраняем текущую дату
      initialDate.value = value
    }
  }

  /**
   * Генерация вариантов времени (каждые 15 минут).
   * При необходимости — фильтруем по минимально допустимому времени (minTime).
   */
  const timeOptions = computed(() => {
    const allOptions = Array.from({ length: 24 }, (_, i) => i)
      .map((hour) =>
        [0, 15, 30, 45].map(
          (minute) => `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`
        )
      )
      .flat()

    const minMinutes = minTime.value.hours * 60 + minTime.value.minutes

    if (
      moment(initialDate.value).format('YYYY-MM-DD') === moment(minDate.value).format('YYYY-MM-DD')
    ) {
      return allOptions.filter((option) => {
        const [hours, minutes] = option.split(':').map(Number)
        const totalMinutes = hours * 60 + minutes
        return totalMinutes >= minMinutes
      })
    }

    return allOptions
  })

  /**
   * Проверка, доступна ли текущая дата для "сегодня" (например, если сейчас ещё не поздний вечер).
   */
  const toDay = computed(() => {
    const rightNow = new Date()
    const nowRome = moment(
      Date.parse(rightNow.toLocaleString('en-US', { timeZone: 'Europe/Rome' }))
    )

    // Если сейчас (по Риму) час < 19, значит сегодня ещё доступно
    return nowRome.hours() < 19
  })

  /**
   * Минимально допустимая дата. Если сейчас ещё "доступно сегодня" — возвращаем сегодня,
   * иначе сдвигаем на завтра.
   */
  const minDate = computed(() => {
    const rightNow = new Date()
    const nowRome = moment(
      Date.parse(rightNow.toLocaleString('en-US', { timeZone: 'Europe/Rome' }))
    )

    if (toDay.value) {
      return nowRome.toDate()
    }

    return nowRome.add(1, 'days').toDate()
  })

  /**
   * Минимально допустимое время (часы и минуты), опираясь на выбранную дату
   * и текущее время в часовом поясе "Europe/Rome".
   */
  const minTime = computed(() => {
    const selectedNow = dateInternal.value ?? new Date()

    const selectedNowRome = moment(
      Date.parse(selectedNow.toLocaleString('en-US', { timeZone: 'Europe/Rome' }))
    )
    const nowRome = moment(
      Date.parse(new Date().toLocaleString('en-US', { timeZone: 'Europe/Rome' }))
    )

    // Если разница в дне (вперёд) ровно 1 день ИЛИ сейчас (по Риму) < 9 утра того же дня
    if (
      selectedNowRome.dayOfYear() - nowRome.dayOfYear() === 1 ||
      (selectedNowRome.dayOfYear() - nowRome.dayOfYear() === 0 && nowRome.hours() < 9)
    ) {
      return { hours: 11, minutes: 0 }
    }

    // Если сегодня (один и тот же день) и "доступно сегодня"
    if (selectedNowRome.dayOfYear() - nowRome.dayOfYear() === 0 && toDay.value) {
      // Ставим плюс 2 часа к текущему
      return { hours: nowRome.hours() + 2, minutes: nowRome.minute() }
    }

    // По умолчанию — время с 00:00
    return { hours: 0, minutes: 0 }
  })

  /**
   * Форматирование даты в удобный для вывода вид (DD/MM/YYYY H:mm).
   */
  const dateFormat = (date) => {
    return moment(date).format('DD/MM/YYYY H:mm')
  }

  // Возвращаем из composable необходимые ссылки и вычисления
  return {
    dateInternal,
    minDate,
    minTime,
    dateTime,
    datePicker,
    initialDate,
    timeSetFirstTime,
    datePickerRef,
    timeOptions,
    handleInternal,
    dateFormat
  }
}
